import { extractString, extractNumber } from '../../utils/sanitizeData';

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  taxRate?: string;
}

interface LineItemsTableProps {
  items?: LineItem[];
  subtotal?: number;
  taxAmount?: number;
  taxRate?: string;
  totalAmount?: number;
  currency?: unknown;
}

export default function LineItemsTable({
  items = [],
  subtotal,
  taxAmount,
  taxRate,
  totalAmount,
  currency,
}: LineItemsTableProps) {
  const safeCurrency = extractString(currency, '');

  const formatPrice = (val: number) => {
    const formatted = val.toFixed(2);
    if (safeCurrency === 'EUR' || safeCurrency === '€') return `€${formatted}`;
    if (safeCurrency === 'USD' || safeCurrency === '$') return `$${formatted}`;
    if (safeCurrency && safeCurrency !== 'UNKNOWN') return `${formatted} ${safeCurrency}`;
    return formatted;
  };

  const safeItems = (items && items.length > 0 ? items : []).map((item, idx) => ({
    id: extractString(item.id, String(idx + 1)),
    description: extractString(item.description, 'Item'),
    quantity: extractNumber(item.quantity, 1),
    unitPrice: extractNumber(item.unitPrice, 0),
    amount: extractNumber(item.amount, 0),
    taxRate: item.taxRate ? extractString(item.taxRate) : undefined,
  }));

  const calculatedSubtotal = safeItems.reduce((acc, it) => acc + it.amount, 0);
  const safeSubtotal = subtotal !== undefined ? extractNumber(subtotal, calculatedSubtotal) : calculatedSubtotal;
  const hasTaxInfo = taxAmount !== undefined && taxAmount !== null;
  const safeTax = hasTaxInfo ? extractNumber(taxAmount, 0) : 0;
  const safeTotal =
    totalAmount !== undefined
      ? extractNumber(totalAmount, safeSubtotal + safeTax)
      : safeSubtotal + safeTax;

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card overflow-hidden mb-6">
      <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-brand-light" />
          <h2 className="text-sm font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
            Extracted Line Items ({safeItems.length})
          </h2>
        </div>
        <span className="text-xs font-mono text-brand-soft dark:text-bodydark2">
          Parsed & Verified
        </span>
      </div>

      {safeItems.length === 0 ? (
        <div className="p-8 text-center text-sm font-mono text-brand-muted dark:text-bodydark">
          No individual line items parsed for this invoice.
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-brand-blue-tint dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark">
                  <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                    Item Description
                  </th>
                  <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-right w-24">
                    Qty
                  </th>
                  <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-right w-32">
                    Unit Price
                  </th>
                  <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-right w-32">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line dark:divide-strokedark text-sm">
                {safeItems.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-brand-bg/60 dark:hover:bg-strokedark/30 transition-colors"
                  >
                    <td className="py-3.5 px-5 text-brand-navy dark:text-white font-medium">
                      {item.description}
                      {item.taxRate && (
                        <span className="ml-2 inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-bg dark:bg-strokedark text-brand-soft">
                          VAT {item.taxRate}
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-brand-muted dark:text-bodydark">
                      {item.quantity}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-brand-muted dark:text-bodydark">
                      {formatPrice(item.unitPrice)}
                    </td>
                    <td className="py-3.5 px-5 text-right font-mono font-bold text-brand-navy dark:text-white">
                      {formatPrice(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Stacked Card View (<=640px) */}
          <div className="block sm:hidden divide-y divide-brand-line dark:divide-strokedark p-3">
            {safeItems.map((item) => (
              <div key={item.id} className="py-3 flex flex-col gap-1.5">
                <div className="text-sm font-semibold text-brand-navy dark:text-white">
                  {item.description}
                </div>
                <div className="flex items-center justify-between text-xs font-mono text-brand-muted dark:text-bodydark">
                  <span>
                    Qty: {item.quantity} × {formatPrice(item.unitPrice)}
                  </span>
                  <span className="font-bold text-brand-navy dark:text-white text-sm">
                    {formatPrice(item.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Table Footer Totals */}
          <div className="bg-brand-bg dark:bg-boxdark-2/60 p-4 sm:p-5 border-t border-brand-line dark:border-strokedark flex flex-col items-end gap-1.5 text-xs font-mono">
            {calculatedSubtotal > 0 && (
              <div className="flex justify-between w-full sm:w-64 text-brand-muted dark:text-bodydark">
                <span>Subtotal:</span>
                <span className="font-semibold text-brand-navy dark:text-white">
                  {formatPrice(safeSubtotal)}
                </span>
              </div>
            )}
            {hasTaxInfo && (
              <div className="flex justify-between w-full sm:w-64 text-brand-muted dark:text-bodydark">
                <span>{taxRate ? `Tax (${taxRate}):` : 'Tax:'}</span>
                <span className="font-semibold text-brand-navy dark:text-white">
                  {formatPrice(safeTax)}
                </span>
              </div>
            )}
            <div className="flex justify-between w-full sm:w-64 text-base font-bold text-brand-navy dark:text-white pt-2 border-t border-brand-line dark:border-strokedark mt-1">
              <span>Total:</span>
              <span className="text-brand-vib dark:text-brand-green">
                {formatPrice(safeTotal)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
